// src/pages/Dashboard.js
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import { StatCard, LoadingScreen } from '../components/ui';

const SIGNS = ['Aries','Taurus','Gemini','Cancer','Leo','Virgo',
               'Libra','Scorpio','Sagittarius','Capricorn','Aquarius','Pisces'];

const SIGN_EMOJI = {
  Aries:'♈', Taurus:'♉', Gemini:'♊', Cancer:'♋',
  Leo:'♌', Virgo:'♍', Libra:'♎', Scorpio:'♏',
  Sagittarius:'♐', Capricorn:'♑', Aquarius:'♒', Pisces:'♓'
};

const QUICK_LINKS = [
  { to: '/kundli',    icon: '📜', label: 'Generate Kundli',    desc: 'Create your birth chart' },
  { to: '/horoscope', icon: '🔭', label: 'Daily Horoscope',    desc: "Read today's prediction" },
  { to: '/match',     icon: '💞', label: 'Compatibility Check', desc: 'Find your perfect match' },
  { to: '/profile',   icon: '👤', label: 'Update Profile',      desc: 'Add your birth details' },
];

export default function Dashboard() {
  const { user }  = useAuth();
  const [stats, setStats] = useState({ kundliCount: 0, matchCount: 0 });
  const [todaySign, setTodaySign] = useState(null);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [kundliRes, matchRes, horoRes] = await Promise.allSettled([
          api.get('/kundli/kundli.php'),
          api.get('/matchmaking/match.php'),
          api.get('/horoscope/horoscope.php?sign=Aries&type=daily'),
        ]);
        setStats({
          kundliCount: kundliRes.value?.data?.kundlis?.length ?? 0,
          matchCount:  matchRes.value?.data?.matches?.length  ?? 0,
        });
        setTodaySign(horoRes.value?.data?.horoscope ?? null);
      } catch(e) {}
      setLoading(false);
    };
    load();
  }, []);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  if (loading) return <LoadingScreen message="Consulting the stars..." />;

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      <div className="cosmic-card p-8 relative overflow-hidden">
        <div className="glow-orb w-64 h-64 bg-cosmic-500/15 -right-10 -top-10" />
        <div className="relative z-10">
          <p className="text-cosmic-400 text-sm mb-1">{greeting},</p>
          <h1 className="font-cinzel text-3xl font-bold text-white mb-2">{user?.name} ✨</h1>
          <p className="text-cosmic-300">Welcome to your cosmic command centre.</p>
          <p className="text-xs text-cosmic-500 mt-3">
            📅 {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon="📜" label="Kundlis Generated" value={stats.kundliCount} sub="Birth charts" />
        <StatCard icon="💞" label="Match Reports"     value={stats.matchCount}  sub="Compatibility checks" />
        <StatCard icon="🔭" label="Today's Sign"      value="Aries"             sub="Featured horoscope" />
        <StatCard icon="⭐" label="Cosmic Score"       value="87%"               sub="Your auspicious rating" />
      </div>

      <div>
        <h2 className="font-cinzel text-xl font-semibold text-white mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {QUICK_LINKS.map(({ to, icon, label, desc }) => (
            <Link key={to} to={to} className="cosmic-card p-5 hover:border-cosmic-500/60 transition-all duration-300 group block">
              <div className="text-3xl mb-3 group-hover:scale-110 transition-transform duration-300">{icon}</div>
              <p className="font-semibold text-white text-sm mb-1">{label}</p>
              <p className="text-xs text-cosmic-400">{desc}</p>
            </Link>
          ))}
        </div>
      </div>

      {todaySign && (
        <div className="cosmic-card p-6 relative overflow-hidden">
          <div className="glow-orb w-48 h-48 bg-gold-500/10 -right-5 -bottom-5" />
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-3xl">{SIGN_EMOJI[todaySign.sign]}</span>
              <div>
                <h2 className="font-cinzel text-lg font-bold text-white">Featured: {todaySign.sign}</h2>
                <p className="text-xs text-cosmic-400">
                  {todaySign.date_range} &bull; {todaySign.element} &bull; {todaySign.ruling_planet}
                </p>
              </div>
            </div>
            <p className="text-cosmic-200 leading-relaxed">{todaySign.prediction}</p>
            <Link to="/horoscope" className="btn-gold inline-flex mt-4 text-sm py-2 px-4">View All Signs &rarr;</Link>
          </div>
        </div>
      )}

      <div>
        <h2 className="font-cinzel text-xl font-semibold text-white mb-4">Zodiac Signs</h2>
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3">
          {SIGNS.map(sign => (
            <Link key={sign} to={"/horoscope?sign=" + sign}
              className="cosmic-card p-3 text-center hover:border-cosmic-400/60 transition-all duration-200 block">
              <div className="text-2xl mb-1">{SIGN_EMOJI[sign]}</div>
              <p className="text-xs text-cosmic-300 font-medium">{sign}</p>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
